import {
  FulfilmentCashOrCollectOption,
  FulfilmentDeliveryResponse,
  FulfilmentNonDeliveryPresentation,
  FulfilmentNonDeliveryResponse,
  FulfilmentSubBasketStockedLocations,
} from '@jbhi-fi/apis/src/fulfilment/types';
import { getStoreFinderUrl } from '@jbhi-fi/utils/src/store/getStoreFinderUrl';
import { sortDeliveryPresentations, OptionNames, getNonDeliveryOptions } from '../../../../api/fulfilment/utils';
import { getStoreClosingTimeFromFulfilmentResponse } from '../../../../utils/store/storeTradingHoursUtils';
import { CARRIER_NAMES } from '../../checkout/constants';
import { parseDeliveryTags } from '../../checkout/utils/parseDeliveryTags';
import { FulfilmentOptionProps } from '../../fulfilment/fulfilment-option/FulfilmentOption';
import { FulfilmentOptionsProps, StoreAvailabilityDetails } from './types';

const parseStoreOption = (
  option: FulfilmentCashOrCollectOption,
  clickAndCollectAvailabile: boolean,
  inStoreAvailable: boolean,
  importantMessaging?: string,
  isAwaitingStock?: boolean
): StoreAvailabilityDetails => {
  const { isStoreOpenInNexthour, tradingHours } = getStoreClosingTimeFromFulfilmentResponse(
    option.location?.tradingHours ?? []
  );
  return {
    id: option.location.id,
    name: option.location?.webStoreDisplayName ?? '',
    distance: option.distance,
    address: option.location.address,
    closingTime: tradingHours,
    isStoreOpenInNexthour,
    isAwaitingStock,
    options: [
      {
        name: 'Click & Collect',
        isAvailable: clickAndCollectAvailabile,
      },
      {
        name: 'In-store',
        isAvailable: inStoreAvailable,
      },
    ],
    importantMessaging: importantMessaging,
  };
};

export const getStoreOptions = (
  clickAndCollectOptions: Array<FulfilmentCashOrCollectOption> | undefined,
  clickAndCollectPresentations: Array<FulfilmentNonDeliveryPresentation> | undefined,
  inStoreOptions: Array<FulfilmentCashOrCollectOption> | undefined,
  maxDistance: number,
  clickAndCollectStockedLocations: Array<FulfilmentSubBasketStockedLocations> | undefined
): Array<StoreAvailabilityDetails> => {
  let pickupInstoreOptions = inStoreOptions ? inStoreOptions.filter((c: any) => c.distance <= maxDistance) : [];
  const cncOptions = clickAndCollectOptions ? clickAndCollectOptions.filter((c: any) => c.distance <= maxDistance) : [];
  let storeList = cncOptions.map(option => {
    const inStoreAvailable = pickupInstoreOptions.some(
      inStoreOption => inStoreOption?.location?.id === option?.location?.id
    );

    if (inStoreAvailable) {
      pickupInstoreOptions = pickupInstoreOptions.filter(
        inStoreOption => inStoreOption?.location?.id !== option?.location?.id
      );
    }
    const presentation = clickAndCollectPresentations?.find(p => p.id === option.id);
    const importantMessaging = presentation?.message;
    const isAwaitingStock = clickAndCollectStockedLocations?.some(
      c => c.locationId === option.location?.id && c.stockStatus === 'AwaitingStock'
    );
    return parseStoreOption(option, true, inStoreAvailable, importantMessaging, isAwaitingStock);
  });
  storeList = storeList.concat(
    pickupInstoreOptions.map(inStoreOption => {
      return parseStoreOption(inStoreOption, false, true);
    })
  );

  return storeList.sort((dis1, dis2) => dis1.distance - dis2.distance);
};

export const generateDeliveryOptions = (delivery: FulfilmentDeliveryResponse): FulfilmentOptionsProps => {
  const isDeliveryAvailable = delivery.deliveryOptions && delivery.deliveryOptions.length > 0;
  const deliveryOptions: Array<FulfilmentOptionProps> = [];
  if (isDeliveryAvailable) {
    const sortedDeliveryOptions = sortDeliveryPresentations(delivery.presentation);

    if (sortedDeliveryOptions.length > 0) {
      sortedDeliveryOptions.forEach(option => {
        const { carrier, price } = option;
        const title = option.deliveryBy?.title;

        deliveryOptions.push({
          title,
          serviceImage: carrier && carrier.imageUrl ? carrier.imageUrl : null,
          serviceName: carrier && carrier.name ? carrier.name : null,
          experienceName: option.experienceName,
          price,
          description: carrier && carrier.name !== CARRIER_NAMES.uber ? option.instructions?.message : null,
          isUnavailable: false,
          tags: parseDeliveryTags(option.tags, false),
        });
      });
    }
  }
  return {
    title: 'Delivery',
    isAvailable: isDeliveryAvailable,
    options: deliveryOptions,
  };
};

export const generateCashOrCollectOptions = (
  data: FulfilmentNonDeliveryResponse,
  optionName: OptionNames,
  maxDistance?: number
): FulfilmentOptionsProps => {
  const filteredOptions =
    getNonDeliveryOptions(data, optionName)?.length > 0 ? getNonDeliveryOptions(data, optionName) : null;
  const presentationOptions = data.presentation && data.presentation.length > 0 ? data.presentation : null;
  const isOptionAvailable =
    filteredOptions && filteredOptions.length > 0 && filteredOptions.every(option => option.location);
  const options = [];
  let isWithinRange = false;

  if (isOptionAvailable) {
    let closestAvailableOption = null;
    const closestLocationId = data.closestLocation ? data.closestLocation.id : null;
    const closestLocationOption = data.clickAndCollectOptions?.find(o => o.location?.id === closestLocationId);
    const closestLocationOptionId = closestLocationOption?.id;
    if (closestLocationId) {
      closestAvailableOption = filteredOptions.find(
        option => option.location && option.location.id === closestLocationId
      );
      const closestLocationPresentation = presentationOptions?.find(p => p.id === closestLocationOptionId);
      const storeName = data.closestLocation?.webStoreDisplayName;

      options.push({
        title: data.closestLocation?.webStoreDisplayName,
        isUnavailable: !closestAvailableOption,
        storeLink: closestAvailableOption ? getStoreFinderUrl(storeName) : null,
        distance: closestAvailableOption?.distance || 0,
        importantMessaging: closestLocationPresentation?.message || '',
      });
      if (closestAvailableOption && closestAvailableOption.distance <= maxDistance) {
        isWithinRange = true;
      }
    }
    if (filteredOptions && filteredOptions.length > 0 && presentationOptions && presentationOptions.length > 0) {
      filteredOptions.forEach(option => {
        if (option.location && option.location.id !== closestLocationId) {
          const singlePresentation = presentationOptions.find(presentation => presentation.id === option.id);
          if (singlePresentation && option.distance <= maxDistance) {
            const storeName = option.location.webStoreDisplayName;
            options.push({
              title: storeName,
              isUnavailable: false,
              storeLink: getStoreFinderUrl(storeName),
              distance: option.distance || 0,
              importantMessaging: singlePresentation.message,
            });
            if (!isWithinRange) {
              isWithinRange = true;
            }
          }
        }
      });
    }
  }

  if (optionName === OptionNames.ClickAndCollect) {
    return {
      title: 'Click & Collect',
      isAvailable: isOptionAvailable,
      isWithinRange,
      options,
    };
  } else {
    return {
      title: 'In-Store',
      isAvailable: isOptionAvailable,
      isWithinRange,
      options,
    };
  }
};

export const formattedUberDeliveryOption = (optionTitle: string): string => {
  const textToRemove = 'Delivered within';
  const withinIndex = optionTitle?.indexOf(textToRemove);
  if (withinIndex !== -1) {
    const timeSubstring =
      optionTitle
        .substring(withinIndex + textToRemove.length)
        ?.replace('minutes', 'min')
        .trim() ?? '';
    optionTitle = `${timeSubstring} delivery`;
  }
  return optionTitle;
};

export const getAvailabilityText = (
  name: string,
  isStoreOpenInNexthour: boolean | undefined,
  isAwaitingStock?: boolean | undefined,
  isClicknCollectAvailable = true
) => {
  const shouldHide1hrCC = window.Jbhifi?.localisationSettings?.shouldHide1hrCC;
  if (name === 'Click & Collect') {
    return `${
      isClicknCollectAvailable && isStoreOpenInNexthour && !isAwaitingStock && !shouldHide1hrCC ? '1 hour ' : ''
    }${name}`;
  }
  return name;
};
